import SectionPanel from '@/components/SectionPanel';
import { SimpleGrid } from '@chakra-ui/react';
import { Select as ReactSelect } from 'chakra-react-select';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { DonorStoreRequest } from 'src/api/DonorStoreRequest';
import CancelButton from 'src/components/buttons/CancelButton';
import SubmitButton from 'src/components/buttons/SubmitButton';
import ErrorMessage from 'src/components/ErrorMessage';
import CountrySelectController from 'src/components/fields/CountrySelect';
import { FormInput } from 'src/components/fields/FormInput';
import { Field } from 'src/components/ui/field';
import { donorService } from 'src/service/DonorService';
import { BiBuilding, BiEnvelope, BiHome, BiMap, BiPhone, BiUser } from 'react-icons/bi';
import { useI18n } from 'src/i18n/I18nContext';
import FormPage from '@/components/FormPage';

type DonorFormValues = {
    kind: DonorStoreRequest['kind'];
    firstName: string;
    lastName: string;
    organizationName: string;
    street: string;
    streetAddition: string;
    postalCode: string;
    city: string;
    countryCode: string;
    email: string;
    phone: string;
};

type DonorKindOption = { value: DonorStoreRequest['kind']; label: string };

const donorKinds: Array<{ value: DonorStoreRequest['kind']; translationKey: string }> = [
    { value: 'person' as DonorStoreRequest['kind'], translationKey: 'person' },
    { value: 'household' as DonorStoreRequest['kind'], translationKey: 'household' },
    { value: 'company' as DonorStoreRequest['kind'], translationKey: 'company' },
    { value: 'foundation' as DonorStoreRequest['kind'], translationKey: 'foundation' },
    { value: 'church' as DonorStoreRequest['kind'], translationKey: 'church' },
    { value: 'municipality' as DonorStoreRequest['kind'], translationKey: 'municipality' },
    { value: 'public-institution' as DonorStoreRequest['kind'], translationKey: 'publicInstitution' },
    { value: 'association' as DonorStoreRequest['kind'], translationKey: 'association' },
    { value: 'other' as DonorStoreRequest['kind'], translationKey: 'other' },
];

const emptyValues: DonorFormValues = {
    kind: 'person' as DonorStoreRequest['kind'],
    firstName: '',
    lastName: '',
    organizationName: '',
    street: '',
    streetAddition: '',
    postalCode: '',
    city: '',
    countryCode: 'DE',
    email: '',
    phone: '',
};

const DonorForm = ({ viewOnly = false }: { viewOnly?: boolean }) => {
    const { language, t } = useI18n();
    const { id } = useParams();
    const donorId = id ? Number(id) : undefined;
    const { control, register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<DonorFormValues>({ defaultValues: emptyValues });
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const donorKind = watch('kind');
    const isPerson = donorKind === 'person';
    const isHousehold = donorKind === 'household';
    const donorKindOptions = useMemo<DonorKindOption[]>(() => donorKinds.map(kind => ({
        value: kind.value,
        label: t(`donors.form.kind.options.${kind.translationKey}`),
    })), [t]);

    useEffect(() => {
        if (!donorId) return;
        void donorService.find(donorId).then(donor => {
            const address = donor.addresses.find(item => item.primary) ?? donor.addresses[0];
            const email = donor.contacts.find(item => item.primary && item.contactType === 'email')?.value ?? '';
            const phone = donor.contacts.find(item => item.primary && item.contactType === 'phone')?.value ?? '';
            reset({ ...emptyValues, kind: donor.kind, firstName: donor.firstName ?? '', lastName: donor.lastName ?? '', organizationName: donor.organizationName ?? '', street: address?.street ?? '', streetAddition: address?.streetAddition ?? '', postalCode: address?.postalCode ?? '', city: address?.city ?? '', countryCode: address?.countryCode ?? 'DE', email, phone });
        }).catch(() => setError('donors.form.errors.load'));
    }, [donorId, reset]);

    const submit = (values: DonorFormValues): Promise<void> => {
        const request: DonorStoreRequest = {
            kind: values.kind,
            firstName: values.firstName || null,
            lastName: values.lastName || null,
            organizationName: values.organizationName || null,
            addresses: values.city ? [{ street: values.street, streetAddition: values.streetAddition || null, postalCode: values.postalCode, city: values.city, countryCode: values.countryCode || 'DE', comments: null, primary: true }] : [],
            contacts: [
                ...(values.email ? [{ contactType: 'email' as DonorStoreRequest['contacts'][number]['contactType'], value: values.email, primary: true }] : []),
                ...(values.phone ? [{ contactType: 'phone' as DonorStoreRequest['contacts'][number]['contactType'], value: values.phone, primary: true }] : []),
            ],
        };
        const store = donorId ? donorService.update(donorId, request) : donorService.create(request).then(() => undefined);
        return store.then(() => navigate('/fundraising/donors')).catch(() => setError('donors.form.errors.save'));
    };

    const heading = viewOnly ? t('donors.form.title.view') : donorId ? t('donors.form.title.edit') : t('donors.form.title.create');
    return (
        <FormPage title={heading} description={t('forms.donors.description')} maxW='5xl'
            onSubmit={handleSubmit(submit)} invalid={!!error}
            actions={<>
                <CancelButton onClick={() => navigate('/fundraising/donors')} />
                {!viewOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {error && <ErrorMessage message={t(error)} />}
            <SectionPanel title={t('forms.donors.details')}>
                <Controller control={control} name='kind' rules={{ required: t('donors.form.kind.required') }} render={({ field, fieldState }) => (
                    <Field id='kind' label={t('donors.form.kind.label')} required disabled={viewOnly} invalid={fieldState.invalid} errorText={fieldState.error?.message}>
                        <ReactSelect<DonorKindOption, false>
                            isSearchable
                            isClearable={false}
                            isDisabled={viewOnly}
                            name={field.name}
                            inputId='kind'
                            ref={field.ref}
                            onBlur={field.onBlur}
                            options={donorKindOptions}
                            value={donorKindOptions.find(option => option.value === field.value)}
                            onChange={option => field.onChange(option?.value)}
                            placeholder={t('donors.form.kind.placeholder')}
                        />
                    </Field>
                )} />

                {(isPerson || isHousehold) ? <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <FormInput name='firstName' label={t('donors.form.fields.firstName')} register={register} error={errors.firstName} required={isPerson} disabled={viewOnly} icon={BiUser} autoFocus />
                    <FormInput name='lastName' label={t('donors.form.fields.lastName')} register={register} error={errors.lastName} required disabled={viewOnly} icon={BiUser} />
                </SimpleGrid> : <FormInput name='organizationName' label={t('donors.form.fields.organizationName')} register={register} error={errors.organizationName} required disabled={viewOnly} icon={BiBuilding} autoFocus />}
            </SectionPanel>
            <SectionPanel title={t('forms.sections.address')}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <FormInput name='street' label={t('donors.form.fields.street')} register={register} error={errors.street} disabled={viewOnly} icon={BiHome} />
                    <FormInput name='streetAddition' label={t('donors.form.fields.streetAddition')} register={register} error={errors.streetAddition} disabled={viewOnly} icon={BiHome} />
                    <FormInput name='postalCode' label={t('donors.form.fields.postalCode')} register={register} error={errors.postalCode} disabled={viewOnly} icon={BiMap} />
                    <FormInput name='city' label={t('donors.form.fields.city')} register={register} error={errors.city} disabled={viewOnly} icon={BiMap} />
                </SimpleGrid>

                <CountrySelectController
                    control={control}
                    name='countryCode'
                    readOnly={viewOnly}
                    isDisabled={viewOnly}
                    countryCodes={['CH', 'DE', 'AT']}
                    label={t('common.country.label')}
                    placeholder={t('common.country.placeholder')}
                    requiredMessage={t('common.country.required')}
                    language={language}
                />
            </SectionPanel>
            <SectionPanel title={t('forms.sections.contact')}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <FormInput name='email' label={t('donors.form.fields.email')} register={register} error={errors.email} disabled={viewOnly} icon={BiEnvelope} type='email' rules={{ pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('donors.form.validation.invalidEmail') } }} />
                    <FormInput name='phone' label={t('donors.form.fields.phone')} register={register} error={errors.phone} disabled={viewOnly} icon={BiPhone} type='tel' rules={{ pattern: { value: /^[+0-9][0-9 ()/.-]*$/, message: t('donors.form.validation.invalidPhone') } }} />
                </SimpleGrid>

            </SectionPanel>
        </FormPage>
    );
};

export default DonorForm;
